import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, PayloadAction } from '@reduxjs/toolkit';
import { EntityState, serializeAxiosError, IQueryParams } from 'app/shared/reducers/reducer.utils';
import { ControlTowerScore, defaultValue, KpiScoreNameList } from 'app/shared/model/control-tower.model';
import { PeriodicType } from 'app/shared/model/enumerations/periodicType';
import { toLocalISOString } from 'app/shared/util/date-utils';

const initialState: EntityState<ControlTowerScore> & { kpiScoreNameList: KpiScoreNameList; tireKpiScoreNameList: string[] } = {
  loading: false,
  loadingList: false,
  errorMessage: null,
  entities: [],
  entitiesAlt: [],
  entity: defaultValue,
  updating: false,
  totalItems: 0,
  updateSuccess: false,
  kpiScoreNameList: { truck: [], implement: [], onChassis: [] },
  tireKpiScoreNameList: [],
};

interface ControlTowerScoreProps extends IQueryParams {
  companyId?: number | null;
  periodicType: PeriodicType;
  licensePlates?: string[] | null;
  sortDirection?: string | null;
  searchCompanyName?: string;
  searchCompanyId?: number | null;
  searchLicensePlate?: string;
  loading?: boolean;
  loadingList?: boolean;
  dateStart: string;
  dateEnd: string;
}

const apiUrl = 'api/control-tower';
let cancelTokenSource = axios.CancelToken.source();

export const getKpiScoreNameList = createAsyncThunk(
  'asset-classification/fetch_control_tower_kpi_score_name_list',
  async () => {
    const requestUrl = `${apiUrl}/kpi-score-names/`;

    const response = await axios.get<KpiScoreNameList>(requestUrl);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getTireKpiScoreNameList = createAsyncThunk(
  'asset-classification/fetch_control_tower_tire_kpi_score_name_list',
  async () => {
    const requestUrl = `${apiUrl}/kpi-tire-score-names/`;

    const response = await axios.get<string[]>(requestUrl);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

export const getAllScoresByCompanyIdAndLicensePlate = createAsyncThunk(
  'truckImplement/fetch_entity_list_search',
  async ({ page, size, sort, sortDirection, searchCompanyId, searchLicensePlate, periodicType, dateStart, dateEnd }: ControlTowerScoreProps) => {
    const requestUrl = `${apiUrl}/general-scores/${periodicType}`;
    const params = {
      page,
      size,
      sort,
      sortDirection,
      searchCompanyId: searchCompanyId || '',
      searchLicensePlate: searchLicensePlate || '',
      dateStart: dateStart ? toLocalISOString(dateStart) : '',
      dateEnd: dateEnd ? toLocalISOString(dateEnd) : '',
      cacheBuster: new Date().getTime(),
    };

    if (cancelTokenSource) {
      cancelTokenSource.cancel('Request canceled...');
    }
    cancelTokenSource = axios.CancelToken.source();

    try {
      const response = await axios.get<any>(requestUrl, {
        params,
        cancelToken: cancelTokenSource.token,
      });
      return response;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.error('Canceled request: ', error.message);
      } else {
        throw error;
      }
    }
  }
);

export const getControlTowerScore = createAsyncThunk(
  'asset-classification/fetch_control_tower_score',
  async ({ companyId, licensePlates, periodicType, dateStart, dateEnd }: ControlTowerScoreProps) => {
    const queryParams = new URLSearchParams();

    queryParams.append('licensePlates', licensePlates.join(','));

    if (periodicType === PeriodicType.CUSTOM) {
      if (dateStart && dateEnd) {
        queryParams.append('dateStart', toLocalISOString(dateStart));
        queryParams.append('dateEnd', toLocalISOString(dateEnd));
      }
    }

    if (companyId !== null) {
      queryParams.append('searchCompanyId', companyId.toString());
    }

    const requestUrl = `${apiUrl}/score/${periodicType}/?${queryParams.toString()}`;

    const response = await axios.get<ControlTowerScore>(requestUrl);
    return response.data;
  },
  { serializeError: serializeAxiosError }
);

// Slice

const controlTowerSlice = createSlice({
  name: 'controlTowerScore',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
    resetList(state) {
      state.entitiesAlt = [];
      state.totalItems = 0;
      state.loadingList = false;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getControlTowerScore.fulfilled, (state, action) => {
        state.loading = false;
        state.entity = action.payload;
      })

      .addCase(getControlTowerScore.pending, state => {
        state.loading = true;
      })
      .addCase(getControlTowerScore.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      })
      .addCase(getKpiScoreNameList.fulfilled, (state, action) => {
        state.loading = false;
        state.kpiScoreNameList = action.payload;
      })
      .addCase(getKpiScoreNameList.pending, state => {
        state.loading = true;
      })
      .addCase(getKpiScoreNameList.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      })
      .addCase(getTireKpiScoreNameList.fulfilled, (state, action) => {
        state.loading = false;
        state.tireKpiScoreNameList = action.payload;
      })
      .addCase(getTireKpiScoreNameList.pending, state => {
        state.loading = true;
      })
      .addCase(getTireKpiScoreNameList.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message || 'Error fetching data';
      })
      .addMatcher(isFulfilled(getAllScoresByCompanyIdAndLicensePlate), (state, action) => {
        return {
          ...state,
          loadingList: false,
          entitiesAlt: action.payload.data.content,
          totalItems: parseInt(action.payload.headers['x-total-count'], 10),
        };
      })
      .addMatcher(isPending(getAllScoresByCompanyIdAndLicensePlate), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.loadingList = true;
      });
  },
});

export const { reset, resetList } = controlTowerSlice.actions;

export default controlTowerSlice.reducer;
